// What a refused broker console action says to the operator (SM-958).
//
// Every broker console mutation that toasts goes through humanizeError
// (main.tsx's MutationCache), and broker-service's refusals carry a typed
// code (BROKER_*), often a details.rule, and an English message written
// for engineers. Before this, an unmapped code fell through to that
// message, so the Thai console toasted English server text. This is the
// one place a broker refusal becomes a sentence: by code and rule first,
// then — for anything on a broker console route that no table knows, a
// backoffice permission refusal included — by HTTP status. It never
// returns the server's message, and every sentence is in a `message`
// key so the page-copy extractor carries it to the Thai catalogue.
//
// Dialogs that explain a refusal in more detail (fees, provider
// settings, receivables, exposure limits) keep their own wording inline;
// the tables they share with the toast are reused here, not copied.

import { APIError } from "@/lib/api";

import { refusalExplanation } from "./fees";
import { brokerErrorDetail, nodeCurrencyRefusal } from "./governance";
import { refusalText } from "./payouts";

type Copy = { message: string };

/** The admin API routes the broker console writes to. */
const BROKER_PATHS = [
  "/admin/brokers",
  "/admin/broker-",
  "/admin/payment-requests",
  "/admin/payout-batches",
  "/admin/provider-recovery",
  "/admin/revenue/adjustments",
  "/admin/revenue/reconciliation",
];

function onBrokerRoute(e: APIError): boolean {
  const path = e.path ?? "";
  return BROKER_PATHS.some((p) => path.startsWith(p));
}

/** BROKER_RECEIVABLE_TRANSITION_INVALID rules; receivables.ts words its dialog refusals with them too. */
export const RECEIVABLE_RULES: Record<string, { message: string }> = {
  NOTE_NOT_CONFIRMED: {
    message: "This payment hasn't been confirmed yet. Confirm receipt under Settlement first, then settle with it.",
  },
  NOTE_NOT_INBOUND: {
    message: "This is a payment we sent to the broker, so it can't settle what the broker owes.",
  },
  NOTE_OTHER_BROKER: {
    message: "This payment was recorded for another broker. Only a payment recorded on this receivable's own broker can settle it.",
  },
  NOTE_CURRENCY_MISMATCH: { message: "This payment is in a different currency from the receivable." },
  NOTE_PAYS_REQUEST: { message: "This payment is linked to a payment request, so it can't also settle a receivable." },
  NOTE_IN_USE: { message: "This payment already settles another receivable." },
  ACTION_PENDING: {
    message: "There is already a settlement or write-off waiting for approval. Approve or reject it first.",
  },
  RESOLVED: { message: "This receivable is already resolved. The screen now shows its latest state." },
};

const REASON_REQUIRED: Copy = { message: "Enter a reason. It is kept in the audit log." };

/** details.rule copy, by code. A rule a code's table does not name falls back to the code's own copy. */
const RULES: Record<string, Record<string, Copy>> = {
  BROKER_APPROVAL_INVALID: {
    NOT_PENDING: { message: "This was already decided, so nothing changed. Reload to see its current state." },
    ALREADY_DECIDED: { message: "You have already decided this request. Each admin decides once." },
    SELF_REJECTION: { message: "You asked for this, so cancel it instead of rejecting it." },
    SELF_APPROVAL: { message: "You asked for this, so another admin must approve it." },
    NOT_REQUESTER: { message: "Only the admin who asked for this can cancel it. To turn it down, reject it instead." },
    SUPERSEDED: { message: "This note was replaced by a correction, so it can't be confirmed. Confirm the correction instead." },
    REQUEST_PENDING: {
      message:
        "This broker already has a raise waiting for approval. Approve, reject or cancel that request before asking for another.",
    },
  },
  BROKER_RECEIVABLE_TRANSITION_INVALID: {
    ...RECEIVABLE_RULES,
    NOT_OPEN: { message: "This receivable can't take that action in its current state. Reload to see it." },
    NOTE_TOO_SMALL: { message: "This payment is smaller than what the broker owes. Choose a larger payment." },
  },
  BROKER_SETTLEMENT_NOTE_CONFLICT: {
    CONFIRMED_IS_FINAL: {
      message: "Once confirmed, a note is final and can't be corrected. Record a note in the opposite direction instead.",
    },
    SUPERSEDED: { message: "This note was already corrected. Correct the newest version instead." },
    NOT_PENDING: { message: "This note is no longer pending, so it can't be changed." },
    SUPERSEDES_OTHER_BROKER: { message: "That note was recorded for another broker." },
    PAYMENT_REQUEST_CHANGED: { message: "Corrections keep the payment request of the note they correct." },
  },
  BROKER_PAYMENT_REQUEST_INVALID_STATE: {
    NOT_REQUESTED: { message: "This payment request is no longer open. Reload the list to see its current state." },
    OTHER_BROKER: { message: "That payment request belongs to another broker." },
  },
  BROKER_PROVIDER_SLUG_CONFLICT: {
    SLUG_TAKEN: { message: "Another broker already uses this launch name." },
    SLUG_IMMUTABLE: { message: "This launch name is already used by the provider and can't change." },
  },
};

/** A code's copy when no rule of its own applies. */
const CODES: Record<string, Copy> = {
  BROKER_IDEMPOTENCY_CONFLICT: {
    message: "This form was already sent with different details. Close it and check the list before recording again.",
  },
  BROKER_IDEMPOTENCY_KEY_REQUIRED: { message: "The console didn't send its request id. Reload the page and try again." },
  BROKER_APPROVAL_INVALID: { message: "This decision can't be made now. Reload to see the request's current state." },
  BROKER_RECEIVABLE_TRANSITION_INVALID: {
    message: "This receivable can't take that action in its current state. Reload to see it.",
  },
  BROKER_SETTLEMENT_NOTE_CONFLICT: { message: "This note can't take that action. Reload the list to see its current state." },
  BROKER_PAYMENT_REQUEST_INVALID_STATE: {
    message: "This payment request can't take that action. Reload the list to see its current state.",
  },
  BROKER_PAYMENT_REQUEST_NOT_FOUND: { message: "That payment request wasn't found. Reload the list and pick it again." },
  BROKER_PAUSED: { message: "This broker, or one above it, is paused, so nothing new can start under it." },
  BROKER_CLOSED: { message: "This broker is closing or closed, so nothing new can start under it." },
  BROKER_NOT_APPROVED: { message: "This broker isn't approved yet, so nothing can start under it." },
  BROKER_SCOPE_DENIED: { message: "That broker or account is outside what this action may reach." },
  BROKER_LIFECYCLE_CONFLICT: {
    message: "Someone changed this broker while you had it open, so nothing was saved. Reload it and try again.",
  },
  BROKER_LIFECYCLE_TRANSITION_INVALID: {
    message: "This broker's status doesn't allow that step. Reload it to see its current status.",
  },
  BROKER_CLOSE_BLOCKED: { message: "This broker still has balances or work to clear, so it can't close yet." },
  BROKER_ACCOUNT_CLOSE_BLOCKED: { message: "This account still holds a balance or work in progress, so it can't close yet." },
  BROKER_HIERARCHY_INVALID: { message: "That change would break the broker tree, so nothing was saved." },
  BROKER_TRADING_MODES_EXCEED_PARENT: { message: "Sub-brokers can only use the trading modes their parent broker uses." },
  BROKER_STAFF_LAST_OWNER: {
    message: "This would leave the broker with no active owner. Make someone else an owner first.",
  },
  BROKER_STAFF_ESCALATION: { message: "That role is above what this account may manage." },
  BROKER_STATE_UNAVAILABLE: {
    message: "The system couldn't read a state this change depends on, so nothing was saved. Try again in a moment.",
  },
  BROKER_ENTRY_PATH_UNSUPPORTED: { message: "This kind of account can't be set up here." },
  BROKER_FUNDING_EDGE_INVALID: {
    message: "Money moves only between a broker and the broker directly above or below it, in the same tree.",
  },
  BROKER_FUNDING_INSUFFICIENT: { message: "The source doesn't have enough available balance in that currency. Nothing moved." },
  BROKER_FUNDING_POOL_MISSING: { message: "One of these brokers has no pool in that currency yet. Nothing moved." },
  BROKER_ALLOCATION_INELIGIBLE: { message: "This account can't receive or return a broker allocation. Nothing moved." },
  BROKER_CREDENTIAL_INVALID: { message: "The broker's server credential isn't valid." },
  BROKER_ACCOUNT_REQUIRED: { message: "This action needs a broker account, and none was found." },
  BROKER_ENFORCE_FLAG_MISMATCH: { message: "This service's enforcement setting doesn't allow that step right now." },
  BROKER_EXPOSURE_EXCEEDED: { message: "This would take the broker's tree past its exposure limit." },
  BROKER_EXPOSURE_UNAVAILABLE: {
    message: "The broker's collateral couldn't be read, so nothing was saved. Try again in a moment.",
  },
  BROKER_COLLATERAL_WITHDRAWAL_REFUSED: {
    message: "Moving this money out would leave a broker above its exposure limit. Nothing moved.",
  },
  BROKER_REVENUE_TRANSITION_INVALID: { message: "This revenue item can't take that action in its current state. Reload to see it." },
  BROKER_REVENUE_INSUFFICIENT: { message: "The broker doesn't have enough available revenue for this. Nothing moved." },
  BROKER_PAYOUT_NOTHING_ELIGIBLE: { message: "There is no available revenue to pay out yet." },
  BROKER_PAYOUT_BATCH_TRANSITION_INVALID: {
    message: "This payout batch can't take that step in its current state. Reload it to see its status.",
  },
  BROKER_CLEARING_INELIGIBLE: { message: "This broker has no clearing account, because it serves no external-credit users." },
  BROKER_CLEARING_UNAVAILABLE: { message: "The clearing balance couldn't be read. Try again in a moment." },
  BROKER_PROVIDER_CONFIG_INVALID: { message: "The system did not accept this provider setting. Check the form and try again." },
  BROKER_PROVIDER_ENABLE_REFUSED: { message: "The provider can't be turned on yet. The dialog lists what is missing." },
  BROKER_PROVIDER_CREDENTIAL_UNAVAILABLE: { message: "The provider's secret key couldn't be found in this environment." },
  BROKER_PROVIDER_CREDENTIAL_WRITE_DISABLED: { message: "Secret keys can't be saved from the console in this environment." },
  // broker-service's generic codes (internal/errs).
  BROKER_000108: { message: "This person already belongs to this broker." },
  BROKER_000109: { message: "The ledger refused this movement. Nothing moved." },
  BROKER_000111: { message: "This covers too many brokers at once. Narrow it down and try again." },
};

/** Fee and revenue-share codes: fees.ts explains them by rule. */
const FEE_CODES = new Set([
  "BROKER_FEE_ASSIGNMENT_MISSING",
  "BROKER_FEE_CEILING_EXCEEDED",
  "BROKER_FEE_PLAN_IMMUTABLE",
  "BROKER_FEE_ASSIGNMENT_CONFLICT",
  "BROKER_FEE_RATE_INVALID",
  "BROKER_FEE_OWNER_MISMATCH",
  "BROKER_FEE_CURRENCY_NOT_TRADED",
  "BROKER_FEE_PLAN_IN_USE",
]);
const SHARE_CODES = new Set(["BROKER_REVENUE_SHARE_INVALID", "BROKER_REVENUE_SHARE_CONFLICT"]);
const FEE_RULE_BROKEN: Copy = { message: "This change breaks a fee or revenue-share rule, so nothing was saved." };

/** What a refusal on a broker route means when no table names its code. */
const BY_STATUS = {
  invalid: { message: "The system didn't accept these values. Check the form and try again." },
  signedOut: { message: "Session expired. Sign in again." },
  forbidden: { message: "You don't have permission to do that." },
  notFound: { message: "This record wasn't found. It may have been removed, so reload the page." },
  conflict: { message: "This changed since you opened it, so nothing was saved. Reload to see its current state, then try again." },
  other: { message: "This action was refused. Reload the page and try again." },
};

/**
 * A broker console refusal in plain words, or null when the error is not
 * one: not an API error, or an uncoded failure (5xx, network) that
 * humanizeError's status copy already covers.
 */
export function brokerRefusalMessage(e: unknown): string | null {
  if (!(e instanceof APIError)) return null;
  const broker = e.code.startsWith("BROKER_");
  if (!broker && !onBrokerRoute(e)) return null;
  if (!broker && (e.status === 0 || e.status >= 500)) return null;
  const rawRule = brokerErrorDetail(e)?.details?.rule;
  const rule = typeof rawRule === "string" ? rawRule : "";
  if (rule === "REASON_REQUIRED") return REASON_REQUIRED.message;
  if (FEE_CODES.has(e.code) || SHARE_CODES.has(e.code)) {
    return refusalExplanation(e, SHARE_CODES.has(e.code) ? "share" : "fee") ?? FEE_RULE_BROKEN.message;
  }
  const byRule = rule ? RULES[e.code]?.[rule] : undefined;
  if (byRule) return byRule.message;
  const byCode = CODES[e.code]?.message ?? nodeCurrencyRefusal(e);
  if (byCode) return byCode;
  if (e.status === 0 || e.status >= 500) return null;
  if (e.status === 401) return BY_STATUS.signedOut.message;
  if (e.status === 403) return BY_STATUS.forbidden.message;
  if (e.status === 404) return BY_STATUS.notFound.message;
  if (e.status === 409) return BY_STATUS.conflict.message;
  if (e.status === 400 || e.status === 422) return BY_STATUS.invalid.message;
  return BY_STATUS.other.message;
}

/**
 * A refusal inside a dialog: the plain sentence, or — for an unconfirmed
 * outcome (5xx, network), which no table words — the server's code and
 * message, which is what the tech team needs.
 */
export function brokerRefusalText(e: unknown): string | null {
  if (!e) return null;
  return brokerRefusalMessage(e) ?? refusalText(e);
}
